import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  VStack,
  HStack,
  Heading,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  useToast,
  TableContainer,
  Select,
  Text,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, ViewIcon, RepeatIcon } from "@chakra-ui/icons";
import { adminApiService } from "../services/adminApiService";
import { Estate, EstateCreate, EstateUpdate, QueryParams } from "../types";

const EstateManagement: React.FC = () => {
  const [estates, setEstates] = useState<Estate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedEstate, setSelectedEstate] = useState<Estate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formData, setFormData] = useState<EstateCreate>({
    name: "",
    odoo_id: undefined,
    company_id: undefined,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const toast = useToast();

  // Fetch estates data
  const fetchEstates = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: QueryParams = {
        search: searchTerm || undefined,
        include_disabled: includeDisabled,
      };
      const response = await adminApiService.getEstates(params);
      setEstates(response.data);
    } catch (error: any) {
      setError("Failed to fetch estates");
      console.error("Error fetching estates:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEstates();
  }, [searchTerm, includeDisabled]);

  const handleOpenModal = (estate?: Estate) => {
    if (estate) {
      setSelectedEstate(estate);
      setFormData({
        name: estate.name,
        odoo_id: estate.odoo_id,
        company_id: estate.company_id,
      });
      setIsEditing(true);
    } else {
      setSelectedEstate(null);
      setFormData({
        name: "",
        odoo_id: undefined,
        company_id: undefined,
      });
      setIsEditing(false);
    }
    onOpen();
  };

  const handleSubmit = async () => {
    setSubmitLoading(true);
    try {
      if (isEditing && selectedEstate) {
        const updateData: EstateUpdate = {
          name: formData.name,
          odoo_id: formData.odoo_id,
          company_id: formData.company_id,
        };
        await adminApiService.updateEstate(selectedEstate.id, updateData);
        toast({
          title: "Success",
          description: "Estate updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        await adminApiService.createEstate(formData);
        toast({
          title: "Success",
          description: "Estate created successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      onClose();
      fetchEstates();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to ${isEditing ? "update" : "create"} estate: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error submitting estate:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (estate: Estate) => {
    try {
      await adminApiService.deleteEstate(estate.id);
      toast({
        title: "Success",
        description: "Estate deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchEstates();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to delete estate: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error deleting estate:", error);
    }
  };

  const handleRestore = async (estate: Estate) => {
    try {
      await adminApiService.restoreEstate(estate.id);
      toast({
        title: "Success",
        description: "Estate restored successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchEstates();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to restore estate: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error restoring estate:", error);
    }
  };

  if (loading) {
    return (
      <Box p={6} textAlign="center">
        <Text>Loading estates...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={4}>Estate Management</Heading>
          
          <HStack spacing={4} mb={4}>
            <Input
              placeholder="Search estates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              maxW="300px"
            />
            <Select
              value={includeDisabled ? "all" : "active"}
              onChange={(e) => setIncludeDisabled(e.target.value === "all")}
              maxW="200px"
            >
              <option value="active">Active Only</option>
              <option value="all">Include Disabled</option>
            </Select>
            <Button colorScheme="blue" onClick={() => handleOpenModal()}>
              Add Estate
            </Button>
          </HStack>
        </Box>

        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Estate Name</Th>
                <Th>Odoo ID</Th>
                <Th>Company ID</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {estates.map((estate) => (
                <Tr key={estate.id}>
                  <Td>{estate.name}</Td>
                  <Td>
                    {estate.odoo_id ? (
                      <Badge colorScheme="blue" variant="outline">
                        {estate.odoo_id}
                      </Badge>
                    ) : (
                      <Text color="gray.500">-</Text>
                    )}
                  </Td>
                  <Td>
                    {estate.company_id ? (
                      <Badge colorScheme="green" variant="outline">
                        {estate.company_id}
                      </Badge>
                    ) : (
                      <Text color="gray.500">-</Text>
                    )}
                  </Td>
                  <Td>
                    <Badge colorScheme={estate.is_disabled ? "red" : "green"}>
                      {estate.is_disabled ? "Disabled" : "Active"}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        aria-label="View"
                        icon={<ViewIcon />}
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenModal(estate)}
                      />
                      {!estate.is_disabled ? (
                        <>
                          <IconButton
                            aria-label="Edit"
                            icon={<EditIcon />}
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => handleOpenModal(estate)}
                          />
                          <IconButton
                            aria-label="Delete"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => handleDelete(estate)}
                          />
                        </>
                      ) : (
                        <IconButton
                          aria-label="Restore"
                          icon={<RepeatIcon />}
                          size="sm"
                          colorScheme="green"
                          variant="outline"
                          onClick={() => handleRestore(estate)}
                        />
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {estates.length === 0 && (
          <Alert status="info">
            <AlertIcon />
            No estates found matching your criteria.
          </Alert>
        )}
      </VStack>

      {/* Estate Form Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditing ? "Edit Estate" : "Add New Estate"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Estate Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter estate name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Odoo ID</FormLabel>
                <Input
                  type="number"
                  value={formData.odoo_id || ""}
                  onChange={(e) => setFormData({...formData, odoo_id: e.target.value ? parseInt(e.target.value) : undefined})}
                  placeholder="Enter Odoo ID (optional)"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Company ID</FormLabel>
                <Input
                  type="number"
                  value={formData.company_id || ""}
                  onChange={(e) => setFormData({...formData, company_id: e.target.value ? parseInt(e.target.value) : undefined})}
                  placeholder="Enter Company ID (optional)"
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSubmit}
              isLoading={submitLoading}
              loadingText={isEditing ? "Updating..." : "Creating..."}
            >
              {isEditing ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default EstateManagement;
